import operatorApiFetch from './apiService';

const returnOrderService = {
  // 獲取退貨單列表（用於表格）
  getRecords: async (params = {}) => {
    const res = await operatorApiFetch('return_orders/get_records', {
      method: 'POST',
      body: params
    });
    return res.json();
  },

  // 獲取所有退貨單 ID（用於篩選）
  getIds: async (params = {}) => {
    const res = await operatorApiFetch('return_orders/get_ids', {
      method: 'POST',
      body: params
    });
    return res.json();
  },

  // 獲取單個退貨單
  get: async (id) => {
    const res = await operatorApiFetch(`return_orders/${id}`, {
      method: 'GET'
    });
    return res.json();
  },

  // 獲取單個退貨單（別名）
  getById: async (id) => {
    return returnOrderService.get(id);
  },

  // 取得建議單號
  suggestName: async () => {
    const res = await operatorApiFetch('return_orders/suggest_name', { method: 'GET' });
    return res.json();
  },

  // 創建退貨單
  create: async (data) => {
    const res = await operatorApiFetch('return_orders', {
      method: 'POST',
      body: { return_order: data }
    });
    return res.json();
  },

  // 從 fulfillment 建立 RO（驗收頁掃到 fulfillment 但還沒有 RO 時，由
  // CreateReturnOrderModal 呼叫）。
  //
  // 不傳 merchant_id：BE 從 fulfillment.merchant 帶出 target merchant，
  // 避免「FE 控制 merchant_id」的風險（operator 對 fulfillment 的存取已
  // 由 current_operator_fulfillments scope 把關，這裡無需再從 FE 補一次）。
  //
  // name 可選 — operator 在 modal 內可改名，留空時 BE 自動產生
  // `${fulfillment.name|number}-R`。
  createFromFulfillment: async ({ fulfillmentId, name }) => {
    const res = await operatorApiFetch('return_orders/create_from_fulfillment', {
      method: 'POST',
      body: { fulfillment_id: fulfillmentId, name: name || null }
    });
    return res.json();
  },

  // 更新退貨單
  update: async (id, data) => {
    const res = await operatorApiFetch(`return_orders/${id}`, {
      method: 'PUT',
      body: { return_order: data }
    });
    return res.json();
  },

  // 刪除退貨單
  delete: async (id) => {
    const res = await operatorApiFetch(`return_orders/${id}`, {
      method: 'DELETE'
    });
    return res.json();
  },

  // 驗證退貨
  verify: async (id) => {
    const res = await operatorApiFetch(`return_orders/${id}/verify`, {
      method: 'POST'
    });
    return res.json();
  },

  // 完成入庫
  store: async (id) => {
    const res = await operatorApiFetch(`return_orders/${id}/store`, {
      method: 'POST'
    });
    return res.json();
  },

  // 完成驗收（finalize receive）
  finalizeReceive: async (id) => {
    const res = await operatorApiFetch(`return_orders/${id}/finalize_receive`, {
      method: 'POST'
    });
    return res.json();
  },

  // 撤銷完成驗收（unfinalize receive）
  unfinalizeReceive: async (id) => {
    const res = await operatorApiFetch(`return_orders/${id}/unfinalize_receive`, {
      method: 'POST'
    });
    return res.json();
  },

  // 取消退貨單
  cancel: async (id) => {
    const res = await operatorApiFetch(`return_orders/${id}/cancel`, {
      method: 'POST'
    });
    return res.json();
  },

  // 恢復退貨單
  resume: async (id) => {
    const res = await operatorApiFetch(`return_orders/${id}/resume`, {
      method: 'POST'
    });
    return res.json();
  },

  // 獲取驗收影片（從關聯的 ReturnReceipt）
  getVideos: async (id) => {
    const res = await operatorApiFetch(`return_orders/${id}/videos`, {
      method: 'GET'
    });
    return res.json();
  },

  // 取得退貨單下所有 receipts 的序號（跨 receipts 彙總）
  getSerialNumbers: async (returnOrderId, params = {}) => {
    const res = await operatorApiFetch(`return_orders/${returnOrderId}/serial_numbers_get_records`, {
      method: 'POST',
      body: { ...params }
    });
    return res.json();
  },

  // 匯出
  export: async (ids) => {
    const res = await operatorApiFetch('return_orders/export', {
      method: 'POST',
      body: { ids }
    });
    return res.json();
  },

  // 獲取訂單選項（用於 Select - 游標分頁）
  getOrders: async ({ search = '', sinceId = null, pageSize = 20 } = {}) => {
    const params = new URLSearchParams({
      page_size: pageSize,
      direction: 'desc'
    });

    if (search) {
      params.append('search', search);
    }

    if (sinceId) {
      params.append('since_id', sinceId);
    }

    const res = await operatorApiFetch(`orders?${params.toString()}`, {
      method: 'GET'
    });
    const response = await res.json();

    if (response.success && response.data) {
      return {
        options: response.data.map(order => ({
          label: order.name || `Order #${order.id}`,
          value: order.id
        })),
        hasMore: response.has_more,
        sinceId: response.since_id
      };
    }

    return { options: [], hasMore: false, sinceId: null };
  },

  // 獲取狀態選項
  getStatuses: async () => {
    return ['pending', 'verified', 'stored', 'canceled'];
  },

  // 獲取商品選項（用於 Select - 游標分頁）
  getProducts: async ({ search = '', sinceId = null, pageSize = 20 } = {}) => {
    const params = new URLSearchParams({
      page_size: pageSize,
      direction: 'desc'
    });

    if (search) {
      params.append('search', search);
    }

    if (sinceId) {
      params.append('since_id', sinceId);
    }

    const res = await operatorApiFetch(`products?${params.toString()}`, {
      method: 'GET'
    });
    const response = await res.json();

    if (response.success && response.data) {
      return {
        options: response.data.map(product => ({
          label: `${product.sku} - ${product.name}`,
          value: product.id,
          sku: product.sku,
          name: product.name,
          validity_days: product.validity_days || 0,
          manufacturing_date_required: product.manufacturing_date_required
        })),
        hasMore: response.has_more,
        sinceId: response.since_id
      };
    }

    return { options: [], hasMore: false, sinceId: null };
  },

  // 匯入退貨單
  import: async (formData) => {
    const response = await operatorApiFetch('return_orders/import', {
      method: 'POST',
      body: formData
    });
    return response.json();
  },

  // 下載匯入模板
  downloadImportTemplate: async (locale, merchantId) => {
    const body = { type: 'return_order', locale };
    if (merchantId) {
      body.merchant_id = merchantId;
    }
    return operatorApiFetch('import_templates/download', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });
  }
};

export { returnOrderService };
export default returnOrderService;
